import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import {
  AnalyticsWorkflowError,
  isPublishPollTerminal,
  toAnalyticsWorkflowError,
} from "../lib/analyticsWorkflow";
import {
  analyticsKeyPrefix,
  analyticsPath,
  type AnalyticsOwner,
} from "../lib/analyticsOwner";
import { useHookCache, useHookCacheQuery } from "../lib/hookCache";
import type { UseHookCacheQueryResult } from "../lib/hookCache";
import {
  analyticsBindingAndStatusSchema,
  analyticsCategoryListSchema,
  analyticsConfigureResponseSchema,
  analyticsFileParametersResponseSchema,
  analyticsInputBindingsResponseSchema,
  analyticsOutputDownloadUrlSchema,
  analyticsRunManifestResponseSchema,
  analyticsRunStepsResponseSchema,
  analyticsStatusSchema,
  analyticsWorkflowLinkSchema,
  analyticsWorkflowListSchema,
  type AnalyticsBindingAndStatus,
  type AnalyticsCategoryList,
  type AnalyticsConfigureResponse,
  type AnalyticsFileParameter,
  type AnalyticsFileParametersResponse,
  type AnalyticsInputBindingsResponse,
  type AnalyticsRunManifestResponse,
  type AnalyticsRunStepsResponse,
  type AnalyticsStatus,
  type AnalyticsWorkflowLink,
  type AnalyticsWorkflowList,
} from "../models/audit/analyticsWorkflow";

/**
 * The one frontend entry point to the analytics workflow routes, so every
 * surface reads the same Zod-parsed shapes and the same cache keys.
 *
 * **There is no detach.** `attach` swaps the workflow; it does not return a
 * column to unattached, and no route does. Nothing here exposes one until an
 * endpoint exists.
 *
 * Cache keys are arrays namespaced by the owner then sub-resource, under the
 * prefix `analyticsKeyPrefix` builds (`["control", controlId, "analytics"]` for a
 * control). Every write invalidates the owner's whole prefix: a configure or an attach can change the bound workflow, the parameter schema
 * projected from it, the stored bindings and the derived status together, so
 * invalidating them individually would leave one of the four stale.
 */

/** How often the publish poll re-reads the status. */
export const ANALYTICS_PUBLISH_POLL_INTERVAL_MS = 10_000;

/**
 * After the poll has run this long with no terminal status, it backs off to
 * `ANALYTICS_PUBLISH_POLL_BACKOFF_INTERVAL_MS`. `isVerified` (test complete,
 * review complete, effectiveness effective) is other people's work and can
 * take days, so a dialog left open past publication has no realistic end —
 * see the note on `isPublishPollTerminal`. A cap was considered and rejected:
 * it would flip `isAwaitingPublish` false while the workflow is genuinely
 * still unverified, changing the copy the auditor sees. Backing off only
 * changes cadence.
 */
export const ANALYTICS_PUBLISH_POLL_BACKOFF_AFTER_MS = 60_000;

/** The slower interval the publish poll backs off to. See the constant above. */
export const ANALYTICS_PUBLISH_POLL_BACKOFF_INTERVAL_MS = 60_000;

// Every read is disabled without an owner, so what these two answer for a
// missing one is never fetched and never cached.
function pathFor(owner: AnalyticsOwner | undefined): string {
  return owner ? analyticsPath(owner) : "";
}

function keyFor(owner: AnalyticsOwner | undefined): string[] {
  return owner ? analyticsKeyPrefix(owner) : [];
}

function requireOwner(owner: AnalyticsOwner | undefined): AnalyticsOwner {
  if (!owner) {
    throw new Error(
      "An analytics workflow write needs a control or work step id",
    );
  }
  return owner;
}

/**
 * The owner rebuilt from its two parts, so the callbacks below keep their
 * identity across renders when a caller passes a fresh owner object each time.
 */
function useStableOwner(
  owner: AnalyticsOwner | undefined,
): AnalyticsOwner | undefined {
  const kind = owner?.kind;
  const id = owner?.id;
  return useMemo(() => (kind && id ? { kind, id } : undefined), [kind, id]);
}

/**
 * Issue one analytics request and parse it, or throw an
 * `AnalyticsWorkflowError`.
 *
 * The query string is appended to the path rather than passed as `query`:
 * `fetchFastApi` only serializes `query` for GET, and the input-binding DELETE
 * carries `columnId` there.
 *
 * The parse sits outside the request's `catch`, so an answered call whose body
 * does not match the schema is its own failure rather than the request's. A
 * `ZodError` reaching `toAnalyticsWorkflowError` would travel as the error's
 * `detail`, which the surfaces render verbatim.
 */
export async function requestAnalytics<S extends z.ZodTypeAny>({
  method,
  path,
  query,
  body,
  schema,
}: {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  query?: Record<string, string>;
  body?: Record<string, string | number | boolean | object | null>;
  schema: S;
}): Promise<z.output<S>> {
  const search = query ? new URLSearchParams(query).toString() : "";
  let response: unknown;
  try {
    response = await fetchFastApi({
      method,
      path: search ? `${path}?${search}` : path,
      body,
      throwOnErrorEnvelope: true,
    });
  } catch (error) {
    throw toAnalyticsWorkflowError(error);
  }

  const parsed = schema.safeParse(response);
  if (!parsed.success) {
    // The browser console is the only place the issues survive, matching
    // `fetchFastApi`'s own reporting for an unexpected failure.
    console.error("Unreadable analytics response:", parsed.error.issues);
    // Caller-agnostic on purpose: this helper serves reads and writes, and
    // after a write the honest statement is that the outcome cannot be read
    // here, never that the write failed.
    throw new AnalyticsWorkflowError(
      "unknown",
      0,
      "The server's response could not be read. Refresh the page to see the current state.",
    );
  }
  return parsed.data;
}

/**
 * The published workflows the signed-in user may attach, associated ones first —
 * or, with `associatedOnly`, just the associated ones.
 *
 * Association is read from the workflow engine's own instances for this
 * control's datum, so the list cannot disagree with the tenant the auditor is
 * looking at.
 */
export function useAnalyticsWorkflowOptions(
  ownerArg: AnalyticsOwner | undefined,
  { associatedOnly = false }: { associatedOnly?: boolean } = {},
): UseHookCacheQueryResult<AnalyticsWorkflowList> {
  const owner = useStableOwner(ownerArg);
  const subResource = associatedOnly ? "associated-workflows" : "workflows";
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/${subResource}`,
        schema: analyticsWorkflowListSchema,
      }),
    [owner, subResource],
  );

  return useHookCacheQuery<AnalyticsWorkflowList>({
    key: [...keyFor(owner), subResource],
    fetcher,
    enabled: !!owner,
  });
}

/**
 * The workflow categories the auditor may create a workflow in.
 *
 * Only categories granting creation permission are offered: binding a column to
 * one that does not would surface as a refusal at Generate rather than at the
 * moment of the choice.
 */
export function useAnalyticsCategories(
  ownerArg: AnalyticsOwner | undefined,
): UseHookCacheQueryResult<AnalyticsCategoryList> {
  const owner = useStableOwner(ownerArg);
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/categories`,
        schema: analyticsCategoryListSchema,
      }),
    [owner],
  );

  return useHookCacheQuery<AnalyticsCategoryList>({
    key: [...keyFor(owner), "categories"],
    fetcher,
    enabled: !!owner,
  });
}

export interface UseAnalyticsStatusOptions {
  /**
   * Poll until the auditor's workflow has been published. Off by default: only
   * the surface that deep-linked them out to build it needs to watch for it.
   */
  pollForPublish?: boolean;
  pollIntervalMs?: number;
}

export interface UseAnalyticsStatusResult
  extends UseHookCacheQueryResult<AnalyticsStatus> {
  /** True while the poll is still running, i.e. no publish detected yet. */
  isAwaitingPublish: boolean;
}

/**
 * The column's derived status, optionally polled for publish detection.
 *
 * Polling is the right shape here and only here: publishing happens in the
 * workflow engine, which emits no webhook and no event Midship can subscribe to,
 * so the status read is the only signal. It stops the moment
 * `isPublishPollTerminal` is satisfied, stops on a failed read rather than
 * hammering a route that is refusing, and clears its timer on unmount. It also
 * backs off to a slower cadence after
 * `ANALYTICS_PUBLISH_POLL_BACKOFF_AFTER_MS` — see that constant.
 *
 * This is also the only route that reports `not_entitled`: it degrades to that
 * status instead of failing when the tenant's analytics credential is disabled,
 * where every other analytics route answers 409.
 */
export function useAnalyticsStatus(
  ownerArg: AnalyticsOwner | undefined,
  columnId: string | undefined,
  {
    pollForPublish = false,
    pollIntervalMs = ANALYTICS_PUBLISH_POLL_INTERVAL_MS,
  }: UseAnalyticsStatusOptions = {},
): UseAnalyticsStatusResult {
  const owner = useStableOwner(ownerArg);
  const enabled = !!owner && !!columnId;
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/status`,
        query: { columnId: columnId ?? "" },
        schema: analyticsStatusSchema,
      }),
    [owner, columnId],
  );

  const query = useHookCacheQuery<AnalyticsStatus>({
    key: [...keyFor(owner), "status", columnId ?? ""],
    fetcher,
    enabled,
  });

  const { data, error, refetch } = query;
  const settled =
    data !== undefined &&
    isPublishPollTerminal(
      data.status,
      data.publishedWorkflowUrl,
      data.isVerified,
    );
  const shouldPoll = pollForPublish && enabled && !settled && error === null;

  useEffect(() => {
    if (!shouldPoll) return;
    const startedAt = Date.now();
    let timer: number;
    // Self-rescheduling rather than setInterval, so the delay between ticks
    // can change partway through the poll's life. See
    // ANALYTICS_PUBLISH_POLL_BACKOFF_AFTER_MS.
    const tick = () => {
      void refetch();
      const elapsed = Date.now() - startedAt;
      const nextDelay =
        elapsed >= ANALYTICS_PUBLISH_POLL_BACKOFF_AFTER_MS
          ? ANALYTICS_PUBLISH_POLL_BACKOFF_INTERVAL_MS
          : pollIntervalMs;
      timer = window.setTimeout(tick, nextDelay);
    };
    timer = window.setTimeout(tick, pollIntervalMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldPoll, pollIntervalMs, refetch]);

  return { ...query, isAwaitingPublish: shouldPoll };
}

/**
 * A read that also reports whether it was ever issued.
 *
 * The one bit the raw query cannot supply: a query held disabled answers with
 * no data, not loading and no error, which is byte-identical to a query whose
 * first fetch is still open. A caller that branches loading → error → "nothing"
 * on the result alone states there is nothing over a request it never sent.
 * `issued` is what separates "never asked" from "asked and answered with
 * nothing", and it has to be told rather than read off the result.
 */
export interface AnalyticsIssuedQuery<T> extends UseHookCacheQueryResult<T> {
  /** The query was enabled, so some fetch of it has been started. */
  issued: boolean;
}

/**
 * The activated version's file parameters — one feed each.
 *
 * Re-projected from the workflow on every read, so it cannot drift from it. A
 * column with no published workflow reads as an empty list rather than an error.
 *
 * `enabled` is for a caller holding the column's status: once the status says
 * the workflow is deleted, this read answers `workflowDeleted` with no
 * parameters. A caller whose status is behind the deletion still gets that
 * answer, and must read `workflowDeleted` as the parameters being unknowable,
 * never as the workflow declaring none.
 */
export function useAnalyticsFileParameters(
  ownerArg: AnalyticsOwner | undefined,
  columnId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): AnalyticsIssuedQuery<AnalyticsFileParametersResponse> {
  const owner = useStableOwner(ownerArg);
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/file-parameters`,
        query: { columnId: columnId ?? "" },
        schema: analyticsFileParametersResponseSchema,
      }),
    [owner, columnId],
  );

  // One expression for whether the query runs and for what the caller is told
  // about it, so a surface cannot believe a read is on its way that was never
  // registered.
  const issued = enabled && !!owner && !!columnId;
  const query = useHookCacheQuery<AnalyticsFileParametersResponse>({
    key: [...keyFor(owner), "file-parameters", columnId ?? ""],
    fetcher,
    enabled: issued,
  });
  return { ...query, issued };
}

/** The column's stored parameter assignments, keyed by parameter key. */
export function useAnalyticsInputBindings(
  ownerArg: AnalyticsOwner | undefined,
  columnId: string | undefined,
): AnalyticsIssuedQuery<AnalyticsInputBindingsResponse> {
  const owner = useStableOwner(ownerArg);
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/input-bindings`,
        query: { columnId: columnId ?? "" },
        schema: analyticsInputBindingsResponseSchema,
      }),
    [owner, columnId],
  );

  // One expression for whether the query runs and for what the caller is told
  // about it, so a surface cannot believe a read is on its way that was never
  // registered.
  const issued = !!owner && !!columnId;
  const query = useHookCacheQuery<AnalyticsInputBindingsResponse>({
    key: [...keyFor(owner), "input-bindings", columnId ?? ""],
    fetcher,
    enabled: issued,
  });
  return { ...query, issued };
}

/** One column's declared file parameters beside its stored assignments. */
export interface AnalyticsColumnFeed {
  columnId: string;
  parameters: AnalyticsFileParameter[];
  bindings: AnalyticsInputBindingsResponse["bindings"];
  workflowDeleted: boolean;
}

export interface AnalyticsColumnFeeds {
  columns: AnalyticsColumnFeed[];
}

export interface AnalyticsColumnFeedsQuery
  extends AnalyticsIssuedQuery<AnalyticsColumnFeeds> {
  /**
   * Re-read the feeds and answer with what came back, or null if that read
   * failed.
   *
   * `refetch` cannot serve a caller that must not act on a stale list: it
   * resolves the same way whether the re-read landed or threw, and a throw
   * leaves the previous answer in the cache. Nor can the caller consult the
   * hook's own `data`, which is whatever the render it closed over held. This
   * answers from the entry the re-read just wrote, so a failure is visible and
   * the list is the fresh one.
   */
  reread: () => Promise<AnalyticsColumnFeeds | null>;
}

// One definition for the query's registration and for the re-read below: two
// spellings of the same key would have the re-read reporting on an entry nobody
// renders.
function columnFeedsKey(owner: AnalyticsOwner | undefined, columnKey: string) {
  return [...keyFor(owner), "column-feeds", columnKey];
}

/**
 * Every analytics column's feeds in one query.
 *
 * Both file-binding surfaces need all of a procedure's columns at once — each
 * offers every `(column, param_key)` pair as an assignment target — and a hook
 * cannot be called in a loop, so the fan-out happens inside one fetcher rather
 * than in a component mounted per column.
 *
 * It reads through the same request helper and the same schemas as the
 * single-column hooks, under the same owner prefix (`analyticsKeyPrefix`) that
 * every write invalidates, so the aggregate cannot report a different answer
 * from them.
 */
export function useAnalyticsColumnFeeds(
  ownerArg: AnalyticsOwner | undefined,
  columnIds: readonly string[],
): AnalyticsColumnFeedsQuery {
  const owner = useStableOwner(ownerArg);
  // The joined ids are the only dependency, so a caller passing a fresh array
  // every render does not re-register the query.
  const columnKey = [...columnIds].sort().join(",");
  const cache = useHookCache();
  const fetcher = useCallback(async () => {
    const path = pathFor(owner);
    const ids = columnKey === "" ? [] : columnKey.split(",");
    const columns = await Promise.all(
      ids.map(async (columnId) => {
        const [parameters, bindings] = await Promise.all([
          requestAnalytics({
            method: "GET",
            path: `${path}/file-parameters`,
            query: { columnId },
            schema: analyticsFileParametersResponseSchema,
          }),
          requestAnalytics({
            method: "GET",
            path: `${path}/input-bindings`,
            query: { columnId },
            schema: analyticsInputBindingsResponseSchema,
          }),
        ]);
        return {
          columnId,
          parameters: parameters.parameters,
          bindings: bindings.bindings,
          workflowDeleted: parameters.workflowDeleted,
        };
      }),
    );
    return { columns };
  }, [owner, columnKey]);

  // One expression for whether the query runs and for what callers are told
  // about it. Two spellings of the condition would let a surface believe a read
  // is on its way that this hook never registered.
  const issued = !!owner && columnKey !== "";
  const query = useHookCacheQuery<AnalyticsColumnFeeds>({
    key: columnFeedsKey(owner, columnKey),
    fetcher,
    enabled: issued,
  });

  const reread = useCallback(async () => {
    const key = columnFeedsKey(owner, columnKey);
    await cache.invalidate(key);
    const entry = cache.getEntry<AnalyticsColumnFeeds>(key);
    if (entry === undefined || entry.error !== null) return null;
    return entry.data ?? null;
  }, [cache, owner, columnKey]);

  return { ...query, issued, reread };
}

/**
 * What one cell's run did, as recorded at the close of the resolve stage.
 *
 * A 404 here is the ordinary "this cell has no recorded run", not a mismatched
 * id — the same code means different things on this route and on the
 * configuration routes, which is why the failure carries its status.
 *
 * `enabled` is for a caller that needs only part of the manifest and needs it
 * only in some states: a caller wanting just the deleted-workflow copy holds
 * the read until the column's status says deleted, rather than paying a round
 * trip on every live cell for a field it will not render.
 */
export function useAnalyticsRunManifest(
  ownerArg: AnalyticsOwner | undefined,
  columnId: string | undefined,
  sourceRowId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UseHookCacheQueryResult<AnalyticsRunManifestResponse> {
  const owner = useStableOwner(ownerArg);
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/run-manifest`,
        query: { columnId: columnId ?? "", sourceRowId: sourceRowId ?? "" },
        schema: analyticsRunManifestResponseSchema,
      }),
    [owner, columnId, sourceRowId],
  );

  return useHookCacheQuery<AnalyticsRunManifestResponse>({
    key: [...keyFor(owner), "run-manifest", columnId ?? "", sourceRowId ?? ""],
    fetcher,
    enabled: enabled && !!owner && !!columnId && !!sourceRowId,
  });
}

/**
 * The run's steps, under phase headings naming the feeds they consumed.
 *
 * `enabled` is for a caller holding the column's status: the route reads the
 * workflow's instance, which a deletion upstream removes, so it answers a
 * deleted workflow with a 502 — and a failed read here toasts.
 */
export function useAnalyticsRunSteps(
  ownerArg: AnalyticsOwner | undefined,
  columnId: string | undefined,
  sourceRowId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): UseHookCacheQueryResult<AnalyticsRunStepsResponse> {
  const owner = useStableOwner(ownerArg);
  const fetcher = useCallback(
    () =>
      requestAnalytics({
        method: "GET",
        path: `${pathFor(owner)}/run-steps`,
        query: { columnId: columnId ?? "", sourceRowId: sourceRowId ?? "" },
        schema: analyticsRunStepsResponseSchema,
      }),
    [owner, columnId, sourceRowId],
  );

  return useHookCacheQuery<AnalyticsRunStepsResponse>({
    key: [...keyFor(owner), "run-steps", columnId ?? "", sourceRowId ?? ""],
    fetcher,
    enabled: enabled && !!owner && !!columnId && !!sourceRowId,
  });
}

/**
 * Mint a signed URL for one of a cell's published outputs.
 *
 * A function rather than a query, because the URL is a capability with an expiry
 * rather than data: caching it would hand a later reader a link that has since
 * gone stale, and the route mints it behind a permission the listing does not
 * need. The caller asks at the moment the auditor asks for the file.
 *
 * The cell is named because the file id is resolved through its manifest — the
 * id is AuditBoard's, and only the manifest says which of those this control is
 * entitled to.
 */
export function useAnalyticsOutputDownloadUrl(
  ownerArg: AnalyticsOwner | undefined,
): (input: {
  fileId: string;
  columnId: string;
  sourceRowId: string;
}) => Promise<string> {
  const owner = useStableOwner(ownerArg);
  return useCallback(
    async ({ fileId, columnId, sourceRowId }) => {
      const path = `${analyticsPath(requireOwner(owner))}/outputs/${encodeURIComponent(fileId)}/download-url`;
      const response = await requestAnalytics({
        method: "GET",
        path,
        query: { columnId, sourceRowId },
        schema: analyticsOutputDownloadUrlSchema,
      });
      return response.url;
    },
    [owner],
  );
}

export interface ConfigureAnalyticsWorkflowInput {
  columnId: string;
  workflowName: string;
  draftedTest: string;
  categoryId: number;
}

/**
 * One parameter assignment.
 *
 * The union is what makes the backend's refusal unreachable from here: neither
 * resolved source names a document, so a `documentId` beside one is a compile
 * error rather than a 422.
 *
 * `sample_export` names none because the file is whichever one the test table
 * marks as its analytics input. That flag rides the document's row on the session
 * rather than any per-sample match, so the binding holds one state per document
 * per test table and one file answers the parameter for every sample of the
 * column. `sample_row` names none because the file is the sample's own row, built
 * from the population when the scope is resolved and belonging to no document.
 */
export type BindAnalyticsParameterInput = {
  columnId: string;
  paramKey: string;
} & (
  | { source: "document"; documentId: string }
  | { source: "sample_export" }
  | { source: "sample_row" }
);

/**
 * The request body for one assignment.
 *
 * Built key by key rather than spread: the route forbids unknown keys and reads
 * a present `documentId` on a resolved binding as a contradiction, so it is
 * omitted for a source that names no document rather than sent as null.
 */
function bindParameterBody(
  input: BindAnalyticsParameterInput,
): Record<string, string | number | boolean | null> {
  const body: Record<string, string | number | boolean | null> = {
    columnId: input.columnId,
    paramKey: input.paramKey,
    source: input.source,
  };
  if (input.source === "document") {
    body.documentId = input.documentId;
  }
  return body;
}

export interface AnalyticsWorkflowActions {
  configure: (
    input: ConfigureAnalyticsWorkflowInput,
  ) => Promise<AnalyticsConfigureResponse>;
  attach: (
    columnId: string,
    automationWorkflowId: number,
  ) => Promise<AnalyticsBindingAndStatus>;
  bindParameter: (
    input: BindAnalyticsParameterInput,
  ) => Promise<AnalyticsInputBindingsResponse>;
  clearParameter: (
    columnId: string,
    paramKey: string,
  ) => Promise<AnalyticsInputBindingsResponse>;
  /**
   * Mint a fresh URL for opening this column's workflow in Optro Analytics.
   *
   * Grouped with the writes despite being a GET, because it is called from a
   * click rather than rendered from cached state, and because what it returns
   * carries a credential that must not be cached. It deliberately does not
   * invalidate: nothing changed.
   */
  workflowLink: (columnId: string) => Promise<AnalyticsWorkflowLink>;
}

/**
 * The writes: create the workflow shell, bind a published workflow, and assign
 * or clear a feed.
 *
 * Each rejects with an `AnalyticsWorkflowError` and shows nothing itself — the
 * banner owns the copy for every failure state, and a toast here would either
 * duplicate it or contradict it.
 */
export function useAnalyticsWorkflowActions(
  ownerArg: AnalyticsOwner | undefined,
): AnalyticsWorkflowActions {
  const owner = useStableOwner(ownerArg);
  const { invalidate } = useHookCache();

  const withInvalidation = useCallback(
    async <T>(request: () => Promise<T>): Promise<T> => {
      const result = await request();
      if (owner) await invalidate(analyticsKeyPrefix(owner));
      return result;
    },
    [owner, invalidate],
  );

  const configure = useCallback(
    (input: ConfigureAnalyticsWorkflowInput) =>
      withInvalidation(() =>
        requestAnalytics({
          method: "POST",
          path: `${analyticsPath(requireOwner(owner))}/configure`,
          body: { ...input },
          schema: analyticsConfigureResponseSchema,
        }),
      ),
    [owner, withInvalidation],
  );

  const attach = useCallback(
    (columnId: string, automationWorkflowId: number) =>
      withInvalidation(() =>
        requestAnalytics({
          method: "POST",
          path: `${analyticsPath(requireOwner(owner))}/attach`,
          body: { columnId, automationWorkflowId },
          schema: analyticsBindingAndStatusSchema,
        }),
      ),
    [owner, withInvalidation],
  );

  const bindParameter = useCallback(
    (input: BindAnalyticsParameterInput) =>
      withInvalidation(() =>
        requestAnalytics({
          method: "PUT",
          path: `${analyticsPath(requireOwner(owner))}/input-bindings`,
          body: bindParameterBody(input),
          schema: analyticsInputBindingsResponseSchema,
        }),
      ),
    [owner, withInvalidation],
  );

  const clearParameter = useCallback(
    (columnId: string, paramKey: string) =>
      withInvalidation(() =>
        requestAnalytics({
          method: "DELETE",
          path: `${analyticsPath(requireOwner(owner))}/input-bindings/${encodeURIComponent(paramKey)}`,
          query: { columnId },
          schema: analyticsInputBindingsResponseSchema,
        }),
      ),
    [owner, withInvalidation],
  );

  const workflowLink = useCallback(
    (columnId: string) =>
      // Not wrapped in `withInvalidation`: this reads, mints nothing on the
      // Midship side, and changes no state a cached query describes.
      requestAnalytics({
        method: "GET",
        path: `${analyticsPath(requireOwner(owner))}/workflow-link`,
        query: { columnId },
        schema: analyticsWorkflowLinkSchema,
      }),
    [owner],
  );

  return { configure, attach, bindParameter, clearParameter, workflowLink };
}
